import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'components/buttons'
import { Spinner } from 'components/loaders'
import { useWorkdayEmployeeUpdates } from './hooks'
import { WorkdayValue } from './types'
import {
  WorkdayDataTable,
  WorkdayValueCell,
  compareWorkdayValues,
} from './WorkdayDataTable'

interface Props {
  employeeId: number
  onBack: () => void
}

export const AllUpdatesView: FC<Props> = ({ employeeId, onBack }) => {
  const { t } = useTranslation()
  const { data, isLoading } = useWorkdayEmployeeUpdates(employeeId)

  const columns = useMemo(
    () =>
      (data?.columns ?? []).map(
        (column, index) =>
          ({
            id: column,
            header: column,
            accessorFn: (update) => update[index],
            filterFn: 'includesString',
            sortingFn: compareWorkdayValues,
            cell: ({ getValue }) => <WorkdayValueCell value={getValue()} />,
          } as ColumnDef<WorkdayValue[], WorkdayValue>),
      ),
    [data?.columns],
  )

  return (
    <div className="flex flex-col w-[90vw] h-[70vh] px-6 pb-6 gap-4">
      <div className="flex items-center justify-between">
        <Button variant="outlined" className="px-3" onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {t(
            'features.usersAdmin.userDetails.workdayProfileData.backToProfileData',
          )}
        </Button>
        {data && (
          <div className="text-sm text-neutral-700">
            {t(
              'features.usersAdmin.userDetails.workdayProfileData.updatesCount',
              { count: data.updates.length },
            )}
          </div>
        )}
      </div>
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <div className="flex flex-col flex-grow">
          <WorkdayDataTable
            columns={columns}
            data={data.updates}
            initialSorting={[{ id: 'received_at', desc: true }]}
          />
        </div>
      )}
    </div>
  )
}
